'use client';

import React, { useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import { useRouter, usePathname } from 'next/navigation';
import { Drawer, Box, useMediaQuery, useTheme } from '@mui/material';
import { useAgentContext } from '../../context/AgentContext';
import { useAgentWebSocket } from '../../hooks/useAgentWebSocket';
import { useAiAssistantEnabled } from '../../hooks/useAiAssistantEnabled';
import { subscribe, unsubscribe } from '@/lib/events';
import AgentPanelHeader from './AgentPanelHeader';
import ChatInput from '../Chat/ChatInput';
import WelcomeScreen from '../WelcomeScreen';
import './AgentPanel.scss';

// Dynamic import to avoid ESM-only react-markdown blocking initial render
const ChatContainer = dynamic(() => import('../Chat/ChatContainer'), { ssr: false });

const HEADER_HEIGHT = 64;

export default function AgentPanel() {
  const isEnabled = useAiAssistantEnabled();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const router = useRouter();
  const pathname = usePathname();
  const locale = pathname.split('/')[1] || 'en';
  const { state, closePanel, restoreLastConversation } = useAgentContext();
  const { connect, disconnect } = useAgentWebSocket();

  // Stable refs for connect/disconnect to avoid effect loops
  const connectRef = useRef(connect);
  const disconnectRef = useRef(disconnect);
  connectRef.current = connect;
  disconnectRef.current = disconnect;

  // Stable ref for restoreLastConversation
  const restoreRef = useRef(restoreLastConversation);
  restoreRef.current = restoreLastConversation;

  // Connect WebSocket when panel opens, disconnect when it closes
  useEffect(() => {
    if (state.panelOpen) {
      connectRef.current();
      // Auto-restore last conversation if panel is empty
      if (state.messages.length === 0 && !state.activeConversationId) {
        restoreRef.current();
      }
    } else {
      disconnectRef.current();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.panelOpen]);

  // Agent-triggered page navigation — lives here because AgentPanel is always mounted (root layout).
  // Close the panel first so the user sees the target page, then navigate after a short delay
  // to let React's concurrent render cycle settle (immediate push can get swallowed by streaming).
  const routerRef = useRef(router);
  routerRef.current = router;
  const localeRef = useRef(locale);
  localeRef.current = locale;
  const closePanelRef = useRef(closePanel);
  closePanelRef.current = closePanel;

  useEffect(() => {
    const handleAgentNavigate = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (!detail?.path) return;
      closePanelRef.current();
      const localePath = `/${localeRef.current}${detail.path}`;
      setTimeout(() => routerRef.current.push(localePath), 150);
    };

    subscribe('agent:navigate-to', handleAgentNavigate);
    return () => unsubscribe('agent:navigate-to', handleAgentNavigate);
  }, []);

  if (!isEnabled) return null;

  const hasMessages = state.messages.length > 0 || state.isStreaming;

  return (
    <Drawer
      variant="temporary"
      anchor="right"
      open={state.panelOpen}
      onClose={closePanel}
      ModalProps={{ className: 'agent-panel-wrapper' }}
      PaperProps={{
        className: 'agent-panel',
        sx: {
          width: isMobile ? '100vw' : 400,
          top: `${HEADER_HEIGHT}px`,
          height: `calc(100vh - ${HEADER_HEIGHT}px)`,
          bgcolor: 'var(--secondary-color)',
          borderLeft: '1px solid rgba(244, 246, 255, 0.08)',
        },
      }}
    >
      <AgentPanelHeader />

      <Box className="agent-panel-body">{hasMessages ? <ChatContainer /> : <WelcomeScreen />}</Box>

      <Box className="agent-panel-footer">
        <ChatInput />
      </Box>
    </Drawer>
  );
}
